import { Logo } from 'components/Logo';
import QuickAccessMenu from 'components/QuickAccessMenu';
import SideNavigation from 'components/SideNavigation';
import { useLayout } from 'prodivers/layout';
import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getSetupData } from 'additiv-services/setup';
import { useAuth } from '../domain/Auth/hooks/useAuth';
import { useClientProfile } from '../prodivers/clientProfile';
import { useManagerProfile } from '../prodivers/managerProfile';
import { useDetectedHandheldDevice } from './useDetectedHandheldDevice';
import { useNotifications } from './useNotifications';

// Define the shape of your navigation items
interface NavigationItem {
    title: string;
    link?: string;
    to?: { pathname: string };
    active: boolean;
    name: string;
    submenu?: null | any[];
    disabled?: boolean;
    target?: string;
}

interface UseMainLayoutProps {
    pathname: string;
}

const setupData = getSetupData();

export const useMainLayout = ({ pathname }: UseMainLayoutProps): void => {
    const navigate = useNavigate();
    const { logout } = useAuth();
    const { initHeaderState } = useLayout();
    const iframeURL = useMemo(() => (setupData.IFRAME_CASE_MANAGEMENT_URL), []);

    const additivCreditIframeUrl = setupData.CREDIT_APP_URL;
    const ziagCreditIframeUrl = setupData.CREDIT_APP_ZIAG_URL;
    const isZurichInvest = window.location.href.includes('zurich.ch');
    const creditAppIframeURL = useMemo(() => `${isZurichInvest ? ziagCreditIframeUrl : additivCreditIframeUrl}/credit-requests`, [isZurichInvest]);

    const { t } = useTranslation();
    const { isHandheldSize } = useDetectedHandheldDevice();
    const { data, hasAccessPolicy } = useManagerProfile();

    const name = data?.name;
    const email = data?.email;
    const image = data?.image;

    const { onReadNotification, allNotifications, errorNotifications } = useNotifications();
    const { clientId } = useClientProfile();

    const logoutAction = useCallback(() => logout(), [logout]);

    const [isOpen, setOpen] = useState<boolean>(false);
    const [isOpenProfile, setOpenProfile] = useState<boolean>(false);
    const toggleNavigation = useCallback(() => setOpen((v) => !v), []);

    const current = useMemo(() => pathname.split('/')[1], [pathname]);

    const navigation = useMemo<NavigationItem[]>(() => {
        const navigationHeader: NavigationItem[] = [
            {
                title: t('navigation.sideNav.dashboard'),
                link: '/dashboard',
                active: current === 'dashboard',
                name: 'dashboard',
            },
            {
                title: t('navigation.sideNav.clients'),
                link: '/clients',
                active: current === 'clients',
                name: 'clients',
            },
            {
                title: t('navigation.sideNav.documents'),
                link: '/documents',
                active: current === 'documents',
                submenu: null,
                disabled: !hasAccessPolicy('EnableDocumentLibraryTab'),
                name: 'documents',
            },
            {
                title: t('navigation.sideNav.caseManagement'),
                to: { pathname: iframeURL },
                active: current === 'case-management',
                target: '_blank',
                disabled: !hasAccessPolicy('CaseManagement'),
                name: 'caseManagement',
            },
            {
                title: t('navigation.sideNav.reports'),
                link: '/reports',
                active: current === 'reports',
                name: 'reports',
                disabled: !hasAccessPolicy('EnableGeneralReports'),
            },
            {
                title: t('navigation.sideNav.CreditAppNavigation'),
                to: { pathname: creditAppIframeURL },
                active: false,
                target: '_blank',
                disabled: !setupData?.CREDIT_INTEGRATION_FEATURE_FLAG || !hasAccessPolicy('NavigateFromWealthToCredit'),
                name: 'CreditAppNavigation',
            },
            {
                title: t('navigation.sideNav.systemMessages'),
                link: '/system-messages',
                active: current === 'system-messages',
                name: 'systemMessages',
                disabled: !setupData.SYSTEM_MESSAGE_FEATURE_FLAG || setupData.SYSTEM_MESSAGE_FEATURE_FLAG === 'false' || !hasAccessPolicy('ManageSystemMessages'),
            },
        ];

        return navigationHeader.filter(f => !f.disabled);
    }, [t, pathname, hasAccessPolicy, setupData]);

    const navigationTablet = useMemo(() => (
        isHandheldSize ? (
            <SideNavigation
                isOpen={isOpen}
                current={pathname}
                onClose={() => setOpen(false)}
            />
        ) : null
    ), [isHandheldSize, isOpen, pathname]);

    const profileInfo = useMemo(() => ({
        name,
        image,
        email,
        withAlert: true,
        withEdit: true,
        editText: t('advisoryDashboard.profileMenu.editProfile'),
        onEdit: () => {
            setOpenProfile(false);
            navigate('/edit-profile');
        },
        onLogout: logoutAction,
        logoutText: t('advisoryDashboard.profileMenu.logout'),
        notifications: {
            data: allNotifications,
            onReadNotification,
            emptyMessage: t('advisoryDashboard.profileMenu.notifications.empty'),
            errorMessage: errorNotifications,
        },
        visible: isOpenProfile,
        onVisibleChange: setOpenProfile,
        footer: React.createElement(React.Fragment),
    }), [
        allNotifications,
        email,
        errorNotifications,
        image,
        logoutAction,
        name,
        onReadNotification,
        t,
        isOpenProfile,
        navigate,
    ]);

    useEffect(() => {
        if (!clientId) {
            initHeaderState({
                content: Logo,
                navigation,
                profileInfo,
                navigationTablet,
                quickAccessMenuComponent: QuickAccessMenu,
                onLeftButtonClick: toggleNavigation,
                onRightButtonClick: logoutAction,
            });
        }
    }, [
        logoutAction,
        initHeaderState,
        navigation,
        navigationTablet,
        toggleNavigation,
        profileInfo,
        clientId,
    ]);
};
